/*
 * TT-HEADER
 * Scroll-shrink, overflow-driven collapse, megamenu, mobile drawer.
 *
 * Ported from design/zola/mockups/nav-menu.js and nav-mega.js. Two deliberate
 * departures from the prototypes, both because a Shopify theme is not a canvas:
 *
 *  1. The prototypes polled with setInterval because their nav was React-rendered
 *     after load and could be replaced at any moment. Liquid renders the nav
 *     once, server-side, so this listens for real events instead. The theme
 *     editor is the one case where the section IS replaced - handled by
 *     shopify:section:load rather than by polling.
 *  2. The megamenu is markup, not a JS-built string. This only shows, hides and
 *     positions it.
 *
 * Collapse is measured, never guessed: the menu is a merchant decision, so the
 * width at which it stops fitting differs per store.
 */

class TTHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.tt-nav__bar');
    this.links = this.querySelector('[data-nav-links]');
    this.icons = this.querySelector('[data-nav-icons]');
    this.compactAt = parseInt(this.dataset.compactAt, 10) || 80;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.initMega();
    this.initDrawer();

    this.onScroll();
    this.measure();

    // Fonts land after first paint and change the links' measured width.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.measure());
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  /* ---------------------------------------------------------------- scroll */

  onScroll() {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    this.classList.toggle('is-compact', y > this.compactAt);
    if (this.openPanel) this.placeMega(this.openPanel);
  }

  onResize() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.measure();
      if (this.openPanel) {
        if (window.innerWidth < 900) this.closeMega();
        else this.placeMega(this.openPanel);
      }
    });
  }

  /* --------------------------------------------------------------- collapse */

  measure() {
    if (!this.links || !this.icons || !this.bar) return;

    // Measure from the uncollapsed state, or the links report zero width and
    // the nav can never come back out of collapse.
    this.classList.remove('is-collapsed', 'is-crowded');

    // scrollWidth exceeds clientWidth only once the links genuinely overflow
    // their (overflow: hidden) track. 1px of slack absorbs sub-pixel layout.
    const overflows = this.links.scrollWidth > this.links.clientWidth + 1;
    if (!overflows) return;

    this.classList.add('is-collapsed');

    // Still crowded with the links gone: drop the optional items too.
    if (this.icons.scrollWidth > this.bar.clientWidth - 48) {
      this.classList.add('is-crowded');
    }
  }

  /* ---------------------------------------------------------------- megamenu */

  initMega() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega]'));
    this.openPanel = null;
    this.closeTimer = null;

    this.triggers.forEach((trigger) => {
      const panel = document.getElementById(trigger.dataset.mega);
      if (!panel) return;

      trigger.addEventListener('mouseenter', () => this.openMega(trigger, panel));
      trigger.addEventListener('focus', () => this.openMega(trigger, panel));
      trigger.addEventListener('mouseleave', () => this.scheduleClose());

      // Click is a toggle, so a top-level item with a megamenu is still
      // reachable by keyboard and touch without navigating away.
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openPanel === panel) this.closeMega();
        else this.openMega(trigger, panel);
      });

      panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    });

    // THE BAR ITSELF HOLDS THE MENU OPEN. Leaving the trigger used to start the
    // 160ms close even when the pointer was still inside the nav, on its way
    // down to the panel - and on a full-bleed compact bar that walk is long
    // enough to lose. Josh, 2026-08-20: "There's a gap a user will cross which
    // would hide the menu."
    //
    // Widening the panel to the nav closed the geometric gap; this closes the
    // temporal one. Between the link and the panel there is now nothing that
    // both looks like the menu and behaves like leaving it.
    //
    // The exception is a top-level link that has no megamenu of its own. Moving
    // onto Dealers is a decision about Dealers, and leaving the open panel up
    // over it would read as the nav ignoring the pointer. A link that DOES have
    // one is left alone here: its own mouseenter opens it, which closes this.
    this.addEventListener('mouseover', (event) => {
      if (!this.openPanel) return;
      const link = event.target.closest && event.target.closest('.tt-nav__link');
      if (link && !link.hasAttribute('data-mega')) {
        this.scheduleClose();
        return;
      }
      clearTimeout(this.closeTimer);
    });

    this.addEventListener('mouseleave', () => this.scheduleClose());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeMega();
    });

    // Anything that opens over the megamenu asks for it to get out of the way.
    // The Signature Bundles modal is the first; a <dialog> makes the page behind
    // it inert, so the panel would otherwise sit there frozen open until the
    // modal closed, and reappear under the pointer afterwards.
    document.addEventListener('tt:mega:close', () => this.closeMega());

    // A click anywhere else dismisses it - INCLUDING the transparent strip
    // beside the card, which is inside the panel element but is not the menu.
    // It bridges the pointer's walk from a full-bleed nav to a content-width
    // card; treating a click on it as a click on the menu would leave a band
    // down both sides of the page that swallows clicks and dismisses nothing.
    document.addEventListener('click', (event) => {
      if (!this.openPanel) return;
      if (this.contains(event.target)) return;
      const card = this.openPanel.querySelector('.tt-mega__inner') || this.openPanel;
      if (card.contains(event.target)) return;
      this.closeMega();
    });

    // Focus moving out of both the trigger and the panel closes it.
    document.addEventListener('focusin', (event) => {
      if (!this.openPanel) return;
      if (this.contains(event.target) || this.openPanel.contains(event.target)) return;
      this.closeMega();
    });
  }

  openMega(trigger, panel) {
    clearTimeout(this.closeTimer);
    if (window.innerWidth < 900) return;
    if (this.openPanel && this.openPanel !== panel) this.closeMega();

    panel.hidden = false;
    this.placeMega(panel);
    // Next frame, so the transition runs from the hidden state rather than
    // being skipped by the same-frame style change.
    requestAnimationFrame(() => panel.classList.add('is-open'));

    trigger.setAttribute('aria-expanded', 'true');
    this.openPanel = panel;
    this.openTrigger = trigger;
  }

  closeMega() {
    if (!this.openPanel) return;
    const panel = this.openPanel;
    panel.classList.remove('is-open');

    const hide = () => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    };
    panel.addEventListener('transitionend', hide, { once: true });
    // transitionend never fires under prefers-reduced-motion.
    setTimeout(hide, 300);

    if (this.openTrigger) this.openTrigger.setAttribute('aria-expanded', 'false');
    this.openPanel = null;
    this.openTrigger = null;
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    // Long enough to cross the 10px gap between nav and panel without losing it.
    this.closeTimer = setTimeout(() => this.closeMega(), 160);
  }

  placeMega(panel) {
    const rect = this.getBoundingClientRect();
    const compact = rect.top <= 1;
    const top = Math.round(rect.bottom + (compact ? 0 : parseFloat(getComputedStyle(this).gap) || 0));

    // The ELEMENT spans the nav, in both states. Compact, that is the full
    // window - and it stays that way on purpose. The visible card inside is
    // held to content width by CSS, so what this widens is the region the
    // pointer can be in without the menu deciding it has been left. Without it
    // the leftmost trigger and a content-width card do not touch on a
    // full-bleed bar, and the walk between them closes the menu.
    panel.style.left = `${Math.round(rect.left)}px`;
    panel.style.width = `${Math.round(rect.width)}px`;
    panel.style.top = `${top}px`;

    // The scroll is the card's, so the max-height is too - see tt-header.css.
    const inner = panel.querySelector('.tt-mega__inner');
    (inner || panel).style.maxHeight = `${Math.max(280, window.innerHeight - top - 12)}px`;
  }

  /* ----------------------------------------------------------------- drawer */

  initDrawer() {
    this.drawer = document.getElementById('tt-nav-drawer');
    if (!this.drawer) return;

    this.drawerTrigger = this.querySelector('[data-drawer-open]');

    if (this.drawerTrigger) {
      this.drawerTrigger.addEventListener('click', () => this.openDrawer());
    }

    this.drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = false;
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    if (this.drawerTrigger) this.drawerTrigger.setAttribute('aria-expanded', 'true');

    const close = this.drawer.querySelector('.tt-drawer__close');
    if (close) close.focus();
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';

    if (this.drawerTrigger) {
      this.drawerTrigger.setAttribute('aria-expanded', 'false');
      this.drawerTrigger.focus();
    }

    const hide = () => {
      if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
    };
    const panel = this.drawer.querySelector('.tt-drawer__panel');
    if (panel) panel.addEventListener('transitionend', hide, { once: true });
    setTimeout(hide, 400);
  }
}

if (!customElements.get('tt-header')) {
  customElements.define('tt-header', TTHeader);
}

// The theme editor swaps the section's DOM wholesale. connectedCallback fires
// again for the new element, so this only has to clean up what outlives it.
document.addEventListener('shopify:section:unload', (event) => {
  if (!event.target.querySelector('tt-header')) return;
  document.body.style.overflow = '';
});
